import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Link } from '@mui/material';
import { ABOUT_US_TITLE, COPYRIGHT_TEXT } from '../../constants/strings.constant';
import { baseRoutes } from '../../constants/routes.constants';
import type { LegalSlug } from '../../content/legal';
import { LEGAL_DOCS_MANIFEST } from '../../content/legal/manifest.generated';

// Legal link labels come from the content module (displayTitle) so the footer,
// the page heading, and the requirements email stay in one voice. Routes are
// the frozen baseRoutes for each slug.
const LEGAL_ROUTE: Record<LegalSlug, string> = {
  terms: baseRoutes.terms,
  'privacy-policy': baseRoutes.privacyPolicy,
  'refund-policy': baseRoutes.refundPolicy,
  'cookie-notice': baseRoutes.cookieNotice,
  'accessibility-statement': baseRoutes.accessibilityStatement,
};

// 2026-08-28, Vanja: the footer link should read "Terms of Use", not the full
// "Terms of Use and Content License Terms".
//
// That full string is one field, LEGAL_DOCS_MANIFEST[terms].displayTitle
// (manifest.generated.ts:6, generated from normalize-legal.mjs:38 where the
// comment reads "Vanja's EXACT display title"). It drives THREE surfaces: this
// footer link, the browser tab title on the policy page (LegalDocument
// index.tsx:150), and the "All policies" menu on every legal page
// (LegalPageNav.tsx:185). She asked about the footer only, so displayTitle is
// left alone and the footer overrides its own label here.
//
// The cost of that decoupling, stated plainly: the footer label can now drift
// from displayTitle without anything upstream noticing. The override lives
// beside the map that consumes it, rather than in strings.constant.ts, so the
// divergence is visible in one file instead of split across two - and
// FooterLinks.test.tsx pins both halves, the short label here and the full
// title still reaching the tab and the nav.
//
// Nothing in the policy prose changes. The on-page <h1> is documentTitle, a
// different field, and is untouched.
const FOOTER_LABEL: Partial<Record<LegalSlug, string>> = {
  terms: 'Terms of Use',
};

// 2026-08-28, Vanja: drop the Refund and Cancellation Policy from the footer
// ("you don't typically see it in footers on sites").
//
// It stays reachable from three other places, all without login, which is what
// keeps her own earlier Stripe requirement satisfied: the checkout pages' own
// footer (this component with `checkout`, drawn by CheckoutLayout in
// checkoutRoutes.tsx, whose layout carries no auth guard; until 2026-10-13 a
// one-link mini-footer there), the "All policies" menu on every legal page
// (LegalPageNav.tsx), and /refund-policy directly (homeRoutes.tsx, an
// unguarded child route).
//
// Omitting rather than deleting the LEGAL_ROUTE entry on purpose: the route map
// stays exhaustive over LegalSlug, so adding a document to the manifest still
// fails to compile here until it is given a route.
const FOOTER_OMIT: readonly LegalSlug[] = ['refund-policy'];

// RJ's revised homepage (20 Oct): footer links and copyright in Inter Regular
// 13 px (font guide) and the muted blue-gray (Joey, Q6: "the shared footer",
// so the checkout footer too). It measures 3.52:1 on the ivory, under the
// 4.5:1 for normal text: used as given, recorded as a known gap, as the pink
// links before it were.
const footerTextSx = {
  color: 'brand.mutedBlueGray',
  fontSize: '13px',
  fontWeight: 400,
};

const linkSx = { whiteSpace: 'nowrap' as const, ...footerTextSx };

type FooterProps = {
  // The footer of the payment and thank-you pages (routes/checkoutRoutes.tsx).
  //
  // Vanja's legal email asks for the policy pages to be "footer-linked
  // throughout" (item 1) and for the refund policy to be "linked at checkout"
  // (item 6). Those two pages have their own layout and carried the refund
  // link alone. With this set they carry the site footer's links AND the
  // refund policy, which stays out of the footer everywhere else, as she asked
  // on 2026-08-28.
  //
  // Every link then opens in a new tab, as the refund link always did there: a
  // buyer part way through paying keeps the page they were on.
  checkout?: boolean;
};

export default function Footer({ checkout = false }: FooterProps) {
  const newTab = checkout ? { target: '_blank', rel: 'noopener noreferrer' } : {};

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: '1rem',
        padding: checkout ? { xs: '1.5rem 1rem', md: '1.5rem 3.75rem' } : '1.5rem 3.75rem',
        // No band of its own: the page shows through, under a thin rule in
        // the soft warm border, as the client's home page picture draws it.
        borderTop: '1px solid',
        borderTopColor: 'divider',
        // The copyright line: secondary copy. (The links set their own
        // colour, below.)
        color: 'text.secondary',
        width: '100%',
        margin: '0',
        bottom: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="nav"
        aria-label="Legal"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          rowGap: '0.5rem',
          columnGap: '1.5rem',
        }}
      >
        {/* Links were the hot pink of the client's colour table until RJ's
            revised homepage (20 Oct) drew them in the muted blue-gray; the
            colour and size are in linkSx, above. */}
        <Link
          component={RouterLink}
          to={baseRoutes.aboutUs}
          underline="none"
          variant="body2"
          sx={linkSx}
          {...newTab}
        >
          {ABOUT_US_TITLE}
        </Link>
        {LEGAL_DOCS_MANIFEST.filter((doc) => checkout || !FOOTER_OMIT.includes(doc.slug)).map((doc) => (
          <Link
            key={doc.slug}
            component={RouterLink}
            to={LEGAL_ROUTE[doc.slug]}
            underline="none"
            variant="body2"
            sx={linkSx}
            {...newTab}
          >
            {FOOTER_LABEL[doc.slug] ?? doc.displayTitle}
          </Link>
        ))}
      </Box>

      <Typography variant="body2" color="inherit" sx={{ whiteSpace: 'nowrap', ...footerTextSx }}>
        {COPYRIGHT_TEXT}
      </Typography>
    </Box>
  );
}
